import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Measuring hooks for the Accounts Manager page. They need a real browser layout (ResizeObserver); without one (tests,
// very old browsers) they simply report "unknown" and the page lays out naturally.
//
// ELEMENTS MAY APPEAR LATE. The real page wrapper (GlobalWrapper) renders its content only after a one-second loader, so
// when these hooks first run the elements they measure do not exist yet. They therefore never look an element up once at
// mount: the refs they hand out are callback refs that remember the element in state, and every effect depends on it, so
// measuring starts the moment the element appears (and again if it is ever replaced).
//
// All sizes are LAYOUT sizes (offsetHeight, offsetTop, scrollHeight ...), never getBoundingClientRect: when the page is
// scaled down to fit the screen (useFitToViewport) getBoundingClientRect reports the SCALED size, and a size measured
// from it would be scaled a second time. Layout sizes are the same at any scale.

/**
 * Keep a right-hand column exactly as tall as the table beside it.
 *
 *   const { leftRef, rightRef, height } = useBesideHeight();   // leftRef / rightRef are callback refs
 *   <Box ref={leftRef}>table</Box>  <Box ref={rightRef} sx={{ height: height ?? 'auto' }}>cards</Box>
 *
 * `height` is the left element's height in px while the right element is really BESIDE it (same top, to its right),
 * and null when the layout has wrapped the right column underneath (then it should just take its natural height).
 * `width` is the right element's width in px while beside (null otherwise); `leftHeight` is the left element's height
 * whether or not the right one is beside it.
 * `rightNode` is the right element itself (null until it exists), for useFitDensity.
 * It follows the left element growing or shrinking and the available width changing (window resize, sidebar toggle).
 */
export const useBesideHeight = () => {
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const leftRef = useCallback((el) => setLeft(el), []);
  const rightRef = useCallback((el) => setRight(el), []);
  const [size, setSize] = useState({ height: null, width: null, leftHeight: null });

  useLayoutEffect(() => {
    if (!left || !right || typeof ResizeObserver === 'undefined') return undefined;

    // offsetTop / offsetLeft are measured from the same offsetParent (the flex row must be position: relative)
    const measure = () => {
      const beside = left.offsetHeight > 0 && Math.abs(right.offsetTop - left.offsetTop) < 4 && right.offsetLeft >= left.offsetLeft + left.offsetWidth - 1;
      const next = { height: beside ? left.offsetHeight : null, width: beside ? right.offsetWidth : null, leftHeight: left.offsetHeight || null };
      setSize((prev) => (prev.height === next.height && prev.width === next.width && prev.leftHeight === next.leftHeight ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(left);
    observer.observe(right);
    if (left.parentElement) observer.observe(left.parentElement); // the row's width decides beside vs wrapped
    return () => observer.disconnect();
  }, [left, right]);

  return { leftRef, rightRef, rightNode: right, height: size.height, width: size.width, leftHeight: size.leftHeight };
};

/**
 * Roomy or compact? A column of fixed height holds cards that look best roomy; when the roomy version does not fit
 * (its content is taller than the column) the compact version is used instead. Decided by MEASURING the real content
 * (fonts, month length and the number of alerts all change the heights), never by a guess.
 *
 *   const dense = useFitDensity({ container: rightNode, active: height !== null, resetKey });
 *
 * `container` the column's element (or `containerRef`, a ref object holding it).
 * `active`   only while the column has a fixed height (beside the table); otherwise it is always roomy.
 * `resetKey` anything that changes how tall the content is (width, height, data): the roomy version is tried again
 *            first, and only if it still overflows does it switch - so a screen that gets wider goes back to roomy.
 * It runs before the browser paints, so the roomy-then-compact retry is never seen.
 */
export const useFitDensity = ({ container, containerRef, active, resetKey }) => {
  const [dense, setDense] = useState(false);
  const lastKey = useRef(resetKey);

  // No dependency list on purpose: it re-measures after EVERY render (the content's height can change at any time).
  // It cannot loop - it only ever sets `dense` when the roomy version overflows, or resets it when `resetKey` changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!active) {
      if (dense) setDense(false);
      return;
    }
    if (lastKey.current !== resetKey) {
      lastKey.current = resetKey;
      if (dense) {
        setDense(false); // try the roomy version again before deciding
        return;
      }
    }
    const el = container || (containerRef && containerRef.current);
    if (!dense && el && el.scrollHeight > el.clientHeight + 1) setDense(true);
  });

  return dense;
};

/** The layout height and width in px of an element (null until measured), following it as it is resized. */
export const useElementSize = () => {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ height: null, width: null });
  const ref = useCallback((el) => setNode(el), []);

  useLayoutEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setSize((prev) => (prev.height === node.offsetHeight && prev.width === node.offsetWidth ? prev : { height: node.offsetHeight, width: node.offsetWidth }));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, size.height, size.width];
};

// Where an element starts on the page, from the top of the page (not of the window): the sum of its layout offsets up the
// offsetParent chain, so it does not change when the page - or the app's own scrolling content panel - is scrolled.
const pageTop = (el) => {
  let top = 0;
  for (let node = el; node; node = node.offsetParent) top += node.offsetTop;
  return top;
};

/**
 * Scale a block down so that it fits in the screen's height (the way the browser's own zoom-out would), never below
 * `minScale`, and never above 1 - a screen with room to spare is left alone.
 *
 *   const { fitRef, scale } = useFitToViewport({ contentHeight: tableHeight });   // fitRef is a callback ref
 *   <Box ref={fitRef} sx={{ height: contentHeight * scale }}>  <Box sx={{ transform: `scale(${scale})`, ... }}>
 *
 * fitRef goes on the UNSCALED box that holds the block; `contentHeight` is the block's natural (layout) height. The room
 * is the window's height minus where fitRef starts on the page minus `bottomMargin`. Re-fitted when the window or the page
 * above it changes size. If even `minScale` cannot make it fit, it stops at `minScale` and the page scrolls a little.
 */
// the smallest increase of the scale that is taken (see compute)
const MIN_GROW = 0.02;

export const useFitToViewport = ({ contentHeight, minScale = 0.65, bottomMargin = 24 }) => {
  const [el, setEl] = useState(null);
  const fitRef = useCallback((node) => setEl(node), []); // a callback ref: the box may appear after the first render
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!el || !contentHeight) {
      setScale(1);
      return undefined;
    }
    const compute = () => {
      const room = window.innerHeight - pageTop(el) - bottomMargin;
      const next = Math.min(1, Math.max(minScale, Math.floor((room / contentHeight) * 100) / 100));
      // The block's own size and what sits above it depend on the scale, so the scale can settle exactly on a rounding
      // boundary and flip between two neighbours (0.69 <-> 0.70) on every layout pass - a visible flicker. It may therefore
      // only GROW by a meaningful step; shrinking is always taken at once. That can only err on the side of "fits".
      setScale((prev) => (prev === next || (next > prev && next - prev < MIN_GROW) ? prev : next));
    };
    compute();
    window.addEventListener('resize', compute);
    // the page above (cards, filters) loading or wrapping moves this block down or up
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(compute);
    if (observer) observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', compute);
      if (observer) observer.disconnect();
    };
  }, [el, contentHeight, minScale, bottomMargin]);

  return { fitRef, scale };
};

/**
 * How many columns to lay `count` equal cards out in, given the width they have: as FEW rows as possible while every card
 * stays at least `minCard` wide, with the rows as even as the count allows (8 cards -> 8 across, else 4 + 4, else 3 + 3 + 2 ...).
 * Never a card squeezed below `minCard`, never a row scrolling sideways. Returns null while the width is unknown, so the
 * caller can fall back to plain CSS.
 */
export const balancedColumns = ({ count, width, minCard = 176, gap = 8 }) => {
  if (!count || !width) return null;
  for (let rows = 1; rows <= count; rows += 1) {
    const columns = Math.ceil(count / rows);
    if ((width - (columns - 1) * gap) / columns >= minCard) return columns;
  }
  return 1;
};

/**
 * The scale for the headline cards: the SAME percentage as the table (so they sit in its proportion), but never larger
 * than the width allows for ONE row of cards (`oneRowWidth` = the layout width a single row needs at full size), and never
 * below `minScale` (the table's own floor).
 *
 * It only READS the table's scale and the box's width - neither depends on the cards' scale - and the cards always stay
 * on one row at the scale it returns, so the cards' height changes smoothly with the table's scale and cannot flip between
 * one and two rows (which would make the table's scale chase its own tail).
 */
export const cardsScale = ({ tableScale = 1, width, oneRowWidth, minScale = 0.65 }) => {
  const byWidth = width && oneRowWidth ? width / oneRowWidth : 1;
  return Math.min(1, Math.max(minScale, Math.floor(Math.min(tableScale, byWidth) * 100) / 100));
};
